import { For, Show, createMemo, onCleanup, type Component } from 'solid-js';
import type { PocketId, SpreadAnalysis, WheelDefinition } from '~/domain/types';
import {
  resolvePocketState,
  type PocketVisualState,
  type PreviewState,
} from '~/view/pocketState';
import { DEFAULT_WHEEL_PALETTE, type WheelPalette } from '~/view/palette';
import { annularSector, arcPath, labelFlip, pocketAngle, pocketStep } from '~/view/geometry';

/**
 * The wheel. Pockets are drawn as ring segments in physical order, starting at
 * twelve o'clock and running clockwise — the order comes straight from the
 * wheel definition, never from a layout constant here.
 *
 * Everything outside the ring is diagnostic: a diverging net-per-pocket bar per
 * pocket, and a faint arc spanning each run of uncovered pockets.
 */

const VIEW = 420;
const CX = VIEW / 2;
const CY = VIEW / 2;

const R_HUB = 82;
const R_INNER = 88;
const R_OUTER = 140;
const R_LABEL = 120;
const R_BADGE = 99;
const R_BASELINE = 166;
const BAR_MAX = 20;
const R_GAP_ARC = 198;

const LONG_PRESS_MS = 450;

export interface WheelProps {
  wheel: WheelDefinition;
  analysis: SpreadAnalysis;
  /** Transient hover preview. Never mutates the spread. */
  preview?: PreviewState | null;
  selected?: PocketId | null;
  /** Pointer is over this pocket — a plain affordance, not a §3.1 state. */
  hovered?: PocketId | null;
  palette?: WheelPalette;
  showNetBars?: boolean;
  showGaps?: boolean;
  showChipCounts?: boolean;
  /**
   * Realised hit counts per pocket from a simulation (§7). Drawn as a ring
   * outside the pockets so the user can compare where their coverage sits
   * against where the ball actually landed.
   */
  heatMap?: Readonly<Record<string, number>> | null;
  class?: string;
  onHoverPocket?: (pocketId: PocketId | null) => void;
  onSelectPocket?: (pocketId: PocketId | null) => void;
  /** Touch long-press — the hover-preview equivalent on a touch device. */
  onPreviewPocket?: (pocketId: PocketId | null) => void;
}

interface PocketView {
  id: PocketId;
  colour: string;
  state: PocketVisualState;
  chips: number;
  net: number;
  angle: number;
  start: number;
  end: number;
}

const Wheel: Component<WheelProps> = (props) => {
  const palette = (): WheelPalette => props.palette ?? DEFAULT_WHEEL_PALETTE;

  const baseColour = (colour: 'red' | 'black' | 'green'): string => {
    const p = palette();
    return colour === 'red' ? p.pocketRed : colour === 'black' ? p.pocketBlack : p.pocketGreen;
  };

  const pocketViews = createMemo<PocketView[]>(() => {
    const { wheel, analysis } = props;
    const preview = props.preview ?? null;
    const selected = props.selected ?? null;
    const step = pocketStep(wheel.pocketCount);

    return wheel.ordered.map((pocket) => {
      const chips = analysis.coverage.get(pocket.id)?.chips ?? 0;
      const angle = pocketAngle(pocket.ringIndex, wheel.pocketCount);
      return {
        id: pocket.id,
        colour: baseColour(pocket.colour),
        chips,
        net: analysis.netPerPocket.get(pocket.id) ?? 0,
        angle,
        start: angle - step / 2,
        end: angle + step / 2,
        state: resolvePocketState({
          chips,
          medianChips: analysis.medianChips,
          inPreview: preview?.pockets.has(pocket.id) ?? false,
          previewMode: preview?.mode ?? null,
          selected: selected === pocket.id,
        }),
      };
    });
  });

  const sectorPath = (view: PocketView, rInner = R_INNER, rOuter = R_OUTER): string =>
    annularSector(CX, CY, rInner, rOuter, view.start, view.end);

  /** Bars are scaled against the largest swing so the shape reads at any stake. */
  const netScale = createMemo(() => {
    let max = 0;
    for (const net of props.analysis.netPerPocket.values()) max = Math.max(max, Math.abs(net));
    return max;
  });

  /**
   * Bar length on a signed square-root scale.
   *
   * One heavy straight bet can pay 300 chips while every losing pocket costs
   * 20, and on a linear scale that renders the losses as invisible stubs — the
   * opposite of §3.1's "losing pockets read instantly". Square root keeps the
   * sign, keeps the ordering, and keeps the small side visible. Exact figures
   * are in the hub readout and each pocket's tooltip. See DECISIONS.md D8.
   */
  const barLength = (net: number): number => {
    const max = netScale();
    if (max <= 0 || net === 0) return 0;
    return Math.sqrt(Math.abs(net) / max) * BAR_MAX;
  };

  const selectedView = createMemo(() =>
    pocketViews().find((view) => view.id === props.selected),
  );

  // -- pointer handling -----------------------------------------------------
  // Mouse hovers preview. Touch taps select, and long-press stands in for hover.
  let longPressTimer: ReturnType<typeof setTimeout> | undefined;
  let longPressFired = false;

  const clearLongPress = () => {
    if (longPressTimer !== undefined) clearTimeout(longPressTimer);
    longPressTimer = undefined;
  };
  onCleanup(clearLongPress);

  const handleEnter = (event: PointerEvent, pocketId: PocketId) => {
    if (event.pointerType === 'mouse') props.onHoverPocket?.(pocketId);
  };

  const handleLeave = (event: PointerEvent) => {
    if (event.pointerType === 'mouse') props.onHoverPocket?.(null);
  };

  const handleDown = (event: PointerEvent, pocketId: PocketId) => {
    if (event.pointerType === 'mouse') return;
    longPressFired = false;
    clearLongPress();
    longPressTimer = setTimeout(() => {
      longPressFired = true;
      props.onPreviewPocket?.(pocketId);
    }, LONG_PRESS_MS);
  };

  const handleUp = (event: PointerEvent, pocketId: PocketId) => {
    if (event.pointerType === 'mouse') return;
    clearLongPress();
    if (longPressFired) {
      props.onPreviewPocket?.(null);
    } else {
      props.onSelectPocket?.(props.selected === pocketId ? null : pocketId);
    }
  };

  const handleClick = (event: MouseEvent, pocketId: PocketId) => {
    // Touch already resolved this on pointerup; `detail === 0` marks the
    // synthetic click a touch sequence emits afterwards.
    if (event.detail === 0) return;
    props.onSelectPocket?.(props.selected === pocketId ? null : pocketId);
  };

  const strokeFor = (state: PocketVisualState) => palette().states[state];

  return (
    <svg
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      preserveAspectRatio="xMidYMid meet"
      class={props.class ?? 'h-full w-full'}
      role="img"
      aria-label={`${props.wheel.label} wheel, ${props.analysis.pocketsCovered} of ${props.wheel.pocketCount} pockets covered`}
    >
      {/* Ring backdrop, so anti-aliased seams between pockets stay dark. */}
      <circle cx={CX} cy={CY} r={R_OUTER} fill="var(--color-surface-0)" />

      {/* 1 — base pocket colours. Every overlay above must stay translucent so
          these remain readable. */}
      <g>
        <For each={pocketViews()}>
          {(view) => (
            <path
              d={sectorPath(view)}
              fill={view.colour}
              stroke={palette().separator}
              stroke-width="0.5"
            />
          )}
        </For>
      </g>

      {/* 2 — state overlays. */}
      <g>
        <For each={pocketViews()}>
          {(view) => (
            <Show when={strokeFor(view.state).overlay}>
              {(overlay) => (
                <path
                  d={sectorPath(view)}
                  fill={overlay()}
                  opacity={strokeFor(view.state).overlayOpacity}
                  style={{ transition: `opacity var(--transition-state) ease-out` }}
                />
              )}
            </Show>
          )}
        </For>
      </g>

      {/* 3 — state outlines. Selection is drawn separately so a selected pocket
          keeps whatever coverage fill it already had. */}
      <g fill="none">
        <For each={pocketViews()}>
          {(view) => (
            <Show when={view.state !== 'selected' && strokeFor(view.state).stroke}>
              {(stroke) => (
                <path
                  d={sectorPath(view)}
                  stroke={stroke()}
                  stroke-width={strokeFor(view.state).strokeWidth}
                  stroke-dasharray={strokeFor(view.state).strokeDash ?? undefined}
                  stroke-linejoin="round"
                />
              )}
            </Show>
          )}
        </For>

        {/* Pointer affordance — deliberately lighter than the selection ring. */}
        <Show when={pocketViews().find((view) => view.id === props.hovered)}>
          {(view) => (
            <path
              d={sectorPath(view())}
              stroke="rgba(255,255,255,0.45)"
              stroke-width="1"
              stroke-linejoin="round"
            />
          )}
        </Show>

        {/* 7 — selection ring, additive. */}
        <Show when={selectedView()}>
          {(view) => (
            <path
              d={sectorPath(view(), R_INNER + 1.3, R_OUTER - 1.3)}
              stroke={palette().states.selected.stroke ?? '#fff'}
              stroke-width={palette().states.selected.strokeWidth}
              stroke-linejoin="round"
            />
          )}
        </Show>
      </g>

      {/* 3b — stacking dot: preview coverage landing on a pocket that already
          has chips on it. */}
      <g>
        <For each={pocketViews().filter((view) => view.state === 'hover-add-overlap')}>
          {(view) => {
            const angle = view.angle;
            return (
              <g transform={`rotate(${angle} ${CX} ${CY})`}>
                <circle
                  cx={CX}
                  cy={CY - R_OUTER + 6.5}
                  r="2.6"
                  fill="var(--color-preview-strong)"
                  stroke="var(--color-surface-0)"
                  stroke-width="0.8"
                />
              </g>
            );
          }}
        </For>
      </g>

      {/* 4 — numerals and chip counts, rotated to their segment. */}
      <g class="tnum" text-anchor="middle" dominant-baseline="central">
        <For each={pocketViews()}>
          {(view) => {
            const flip = labelFlip(view.angle);
            return (
              <g transform={`rotate(${view.angle} ${CX} ${CY})`} pointer-events="none">
                <g transform={flip ? `rotate(180 ${CX} ${CY - R_LABEL})` : undefined}>
                  <text
                    x={CX}
                    y={CY - R_LABEL}
                    font-size={view.id.length > 1 ? '10' : '11'}
                    font-weight="600"
                    fill={strokeFor(view.state).labelColour}
                  >
                    {view.id}
                  </text>
                  {/* Rotated, a 6 and a 9 are the same glyph. Underline them the
                      way a real wheel does, or the ring cannot be read at a
                      glance — let alone at arm's length in casino mode. */}
                  <Show when={view.id === '6' || view.id === '9'}>
                    <line
                      x1={CX - 4.5}
                      y1={CY - R_LABEL + 6.5}
                      x2={CX + 4.5}
                      y2={CY - R_LABEL + 6.5}
                      stroke={strokeFor(view.state).labelColour}
                      stroke-width="1.1"
                      stroke-linecap="round"
                    />
                  </Show>
                </g>
                <Show when={(props.showChipCounts ?? true) && view.chips > 0}>
                  <text
                    x={CX}
                    y={CY - R_BADGE}
                    transform={flip ? `rotate(180 ${CX} ${CY - R_BADGE})` : undefined}
                    font-size="7.5"
                    font-weight="500"
                    fill={palette().chipBadge}
                    opacity="0.85"
                  >
                    {view.chips}
                  </text>
                </Show>
              </g>
            );
          }}
        </For>
      </g>

      {/* 5 — net-per-pocket bars, diverging from a zero baseline so losing
          pockets read instantly. */}
      <Show when={(props.showNetBars ?? true) && netScale() > 0}>
        <g>
          <circle
            cx={CX}
            cy={CY}
            r={R_BASELINE}
            fill="none"
            stroke={palette().baselineRing}
            stroke-width="0.8"
          />
          <For each={pocketViews()}>
            {(view) => {
              const end = () =>
                view.net >= 0 ? R_BASELINE + barLength(view.net) : R_BASELINE - barLength(view.net);
              return (
                <Show when={view.net !== 0}>
                  <g transform={`rotate(${view.angle} ${CX} ${CY})`}>
                    <line
                      x1={CX}
                      y1={CY - R_BASELINE}
                      x2={CX}
                      y2={CY - end()}
                      stroke={view.net >= 0 ? palette().gainBar : palette().lossBar}
                      stroke-width="3.4"
                      stroke-linecap="butt"
                    />
                  </g>
                </Show>
              );
            }}
          </For>
        </g>
      </Show>

      {/* 5b — realised hit frequency from a simulation. Deliberately a separate
          ring rather than a recolouring of the pockets, so it can never be
          confused with placed coverage. */}
      <Show when={props.heatMap}>
        {(heat) => {
          const counts = () => Object.values(heat());
          const expected = () =>
            counts().reduce((sum, value) => sum + value, 0) / props.wheel.pocketCount;
          return (
            <g>
              <For each={pocketViews()}>
                {(view) => {
                  const hits = () => heat()[view.id] ?? 0;
                  // Deviation from a uniform wheel, clamped to ±50%.
                  const deviation = () =>
                    expected() === 0 ? 0 : Math.max(-0.5, Math.min(0.5, hits() / expected() - 1));
                  return (
                    <path
                      d={annularSector(CX, CY, R_OUTER + 2, R_OUTER + 9, view.start, view.end)}
                      fill={deviation() >= 0 ? palette().gainBar : palette().lossBar}
                      opacity={0.15 + Math.abs(deviation()) * 1.5}
                    >
                      <title>{`${view.id} — ${hits()} hits`}</title>
                    </path>
                  );
                }}
              </For>
            </g>
          );
        }}
      </Show>

      {/* 6 — ring gaps: the key wheel-side diagnostic, drawn as a faint arc
          spanning each run of uncovered pockets. */}
      <Show when={props.showGaps ?? true}>
        <g fill="none" stroke-linecap="butt">
          <For each={props.analysis.ringGaps}>
            {(gap) => {
              const step = pocketStep(props.wheel.pocketCount);
              const start = (gap.startIndex - 0.5) * step;
              return (
                <path
                  d={arcPath(CX, CY, R_GAP_ARC, start, start + gap.length * step)}
                  stroke={palette().gapArc}
                  stroke-width={gap.length === props.analysis.longestGap?.length ? 4 : 2}
                />
              );
            }}
          </For>
        </g>
      </Show>

      {/* Hub and inspection readout. */}
      <circle cx={CX} cy={CY} r={R_HUB} fill={palette().hub} />
      <circle
        cx={CX}
        cy={CY}
        r={R_HUB}
        fill="none"
        stroke="rgba(255,255,255,0.07)"
        stroke-width="1"
      />
      <Show when={selectedView()}>
        {(view) => (
          <g text-anchor="middle" class="tnum" pointer-events="none">
            <text x={CX} y={CY - 22} font-size="9" fill="var(--color-ink-faint)" letter-spacing="1.4">
              POCKET
            </text>
            <text x={CX} y={CY + 8} font-size="34" font-weight="600" fill="var(--color-ink)">
              {view().id}
            </text>
            <text x={CX} y={CY + 28} font-size="10" fill="var(--color-ink-dim)">
              {view().chips} {view().chips === 1 ? 'chip' : 'chips'}
            </text>
            <text
              x={CX}
              y={CY + 44}
              font-size="12"
              font-weight="600"
              fill={view().net >= 0 ? palette().gainBar : palette().lossBar}
            >
              {view().net >= 0 ? '+' : '−'}
              {Math.abs(view().net)}
            </text>
          </g>
        )}
      </Show>

      {/* Hit targets last, and slightly oversized for touch. */}
      <g fill="transparent" style={{ cursor: 'pointer' }}>
        <For each={pocketViews()}>
          {(view) => (
            <path
              d={sectorPath(view, R_INNER - 5, R_OUTER + 5)}
              onPointerEnter={(event: PointerEvent) => handleEnter(event, view.id)}
              onPointerLeave={handleLeave}
              onPointerDown={(event: PointerEvent) => handleDown(event, view.id)}
              onPointerUp={(event: PointerEvent) => handleUp(event, view.id)}
              onPointerCancel={clearLongPress}
              onClick={(event: MouseEvent) => handleClick(event, view.id)}
            >
              <title>{`${view.id} — ${view.chips} chips, net ${view.net >= 0 ? '+' : ''}${view.net}`}</title>
            </path>
          )}
        </For>
      </g>
    </svg>
  );
};

export default Wheel;
